// Offline-first submission queue, IndexedDB-backed.
//
// Wednesday's Endicott Arm cruise spends ~5 hours out of cell range.
// Players still want to react to a Wilman challenge in that window —
// snap a photo, type a text answer, hit submit — and have the app drain
// the queue when signal returns. This module is the queue.
//
// Each queued record stores enough to replay the entire Phase-3 path:
//   1. POST /api/photo/sign-upload (or text/multi equivalent)
//   2. Upload blob(s) directly to Cloudinary using the signed params
//   3. POST /api/photo/verify (or text/photo-multi-verify) with the URLs
//
// Image bytes live in IndexedDB as Blob entries — Cloudinary's public_id
// + URL aren't known yet (no upload happened) so we store the raw file.

const DB_NAME = 'somesay-v1';
const STORE = 'queue';
const VERSION = 1;
const SYNC_TAG = 'submission-drain';

let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('indexedDB_unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('status', 'status', { unique: false });
        store.createIndex('created_at', 'created_at', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('idb_open_failed'));
  });
  return dbPromise;
}

function tx(db, mode = 'readonly') {
  return db.transaction(STORE, mode).objectStore(STORE);
}

function asPromise(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function uuid() {
  return `q_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

// ---- Public API ----------------------------------------------------

export async function enqueue(record) {
  const db = await openDb();
  const now = new Date().toISOString();
  const full = {
    id: uuid(),
    status: 'queued',
    attempts: 0,
    last_error: null,
    result: null,
    created_at: now,
    updated_at: now,
    ...record,
  };
  await asPromise(tx(db, 'readwrite').put(full));
  await registerBackgroundSync();
  return full;
}

export async function listQueue() {
  const db = await openDb();
  const rows = await asPromise(tx(db).getAll());
  return rows.sort((a, b) => (a.created_at < b.created_at ? -1 : 1));
}

export async function listPending() {
  const all = await listQueue();
  return all.filter((r) => r.status !== 'done' && r.status !== 'failed');
}

export async function getById(id) {
  const db = await openDb();
  return asPromise(tx(db).get(id));
}

export async function update(id, patch) {
  const db = await openDb();
  const store = tx(db, 'readwrite');
  const existing = await asPromise(store.get(id));
  if (!existing) return null;
  const next = { ...existing, ...patch, updated_at: new Date().toISOString() };
  await asPromise(store.put(next));
  return next;
}

export async function remove(id) {
  const db = await openDb();
  await asPromise(tx(db, 'readwrite').delete(id));
}

export async function clearDone() {
  const db = await openDb();
  const all = await asPromise(tx(db).getAll());
  const store = tx(db, 'readwrite');
  for (const r of all) {
    if (r.status === 'done') store.delete(r.id);
  }
}

export async function pendingCount() {
  return (await listPending()).length;
}

// Browser-level connectivity. `online` events are best-effort but
// trustworthy enough to trigger a drain attempt — even if we're wrong,
// the drain just retries the failed pieces.
export function onConnectivityRestore(callback) {
  if (typeof window === 'undefined') return () => {};
  const handler = () => {
    if (navigator.onLine) callback();
  };
  window.addEventListener('online', handler);
  return () => window.removeEventListener('online', handler);
}

export function isOnline() {
  if (typeof navigator === 'undefined') return true;
  return navigator.onLine !== false;
}

// Try to register a Background Sync. If unsupported (Safari), we fall
// back to the `online` event listener — the queue still drains, just
// only when the tab is open.
export async function registerBackgroundSync() {
  if (typeof navigator === 'undefined' || !navigator.serviceWorker) return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    if (reg && 'sync' in reg) {
      await reg.sync.register(SYNC_TAG);
      return true;
    }
  } catch {
    /* sync unsupported — fine, fallback handles it */
  }
  return false;
}

export const SUBMISSION_QUEUE_SYNC_TAG = SYNC_TAG;
